import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import './PrivateLessons.css';
import {
  API_BASE,
  authHeaders,
  canParentChangeLesson,
  formatDateTime,
  statusBadgeClass,
  statusLabel
} from './privateLessonUtils.js';

function AllMyPrivateLessons() {
  const [lessons, setLessons] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [cancelingId, setCancelingId] = useState('');

  const fetchLessons = async () => {
    try {
      setIsLoading(true);
      const res = await axios.get(`${API_BASE}/private-lesson/my`, {
        headers: authHeaders()
      });
      setLessons(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (err) {
      console.error('Error fetching my private lessons', err);
      setLessons([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLessons();
  }, []);

  const nowTime = Date.now();

  const upcomingLessons = useMemo(
    () => lessons
      .filter((item) => item.start_at && new Date(item.start_at).getTime() >= nowTime)
      .sort((a, b) => new Date(a.start_at) - new Date(b.start_at)),
    [lessons, nowTime]
  );

  const pastLessons = useMemo(
    () => lessons
      .filter((item) => item.start_at && new Date(item.start_at).getTime() < nowTime)
      .sort((a, b) => new Date(b.start_at) - new Date(a.start_at)),
    [lessons, nowTime]
  );

  const requestedLessons = useMemo(
    () => upcomingLessons.filter((item) => String(item.status || '').toLowerCase() === 'requested'),
    [upcomingLessons]
  );

  const confirmedLessons = useMemo(
    () => upcomingLessons.filter((item) => String(item.status || '').toLowerCase() !== 'requested'),
    [upcomingLessons]
  );

  const cancelLesson = async (privateLessonId) => {
    const reason = window.prompt('Waarom wil je deze priveles annuleren? Je mag dit ook leeg laten.');
    if (reason === null) return;

    try {
      setCancelingId(privateLessonId);
      await axios.post(
        `${API_BASE}/private-lesson/${privateLessonId}/cancel`,
        { reason: reason || null },
        { headers: authHeaders() }
      );
      alert('Priveles geannuleerd.');
      fetchLessons();
    } catch (err) {
      console.error('Error cancelling private lesson', err);
      alert(err?.response?.data?.error || 'Annuleren mislukt.');
    } finally {
      setCancelingId('');
    }
  };

  const renderLessonsTable = (items, showActions = true) => {
    if (items.length === 0) {
      return <p>Geen privelessen gevonden.</p>;
    }

    return (
      <div className="table-responsive mb-4">
        <table className="table table-bordered align-middle">
          <thead>
            <tr>
              <th>Leerling</th>
              <th>Onderwerp</th>
              <th>Docent</th>
              <th>Datum/tijd</th>
              <th>Duur</th>
              <th>Locatie</th>
              <th>Status</th>
              {showActions && <th>Acties</th>}
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.private_lesson_id}>
                <td>{item.student_name || '-'}</td>
                <td>{item.subject || 'Priveles'}</td>
                <td>{item.docent_name || '-'}</td>
                <td>{formatDateTime(item.start_at)}</td>
                <td>{item.duration_minutes || item.lesson_duration_minutes || '-'} min</td>
                <td>{item.location_name || '-'}</td>
                <td>
                  <span className={statusBadgeClass(item.status)}>{statusLabel(item.status)}</span>
                </td>
                {showActions && (
                  <td>
                    {canParentChangeLesson(item) ? (
                      <div className="d-flex gap-2 flex-wrap">
                        <Link
                          to={`/private-lessons/reschedule/${item.private_lesson_id}`}
                          className="btn btn-outline-primary btn-sm"
                        >
                          Wijzigen
                        </Link>
                        <button
                          type="button"
                          className="btn btn-outline-danger btn-sm"
                          disabled={cancelingId === item.private_lesson_id}
                          onClick={() => cancelLesson(item.private_lesson_id)}
                        >
                          {cancelingId === item.private_lesson_id ? 'Bezig...' : 'Annuleren'}
                        </button>
                      </div>
                    ) : (
                      <span className="text-muted">Geen acties</span>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="bg-white p-4">
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="bg-white p-4">
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
        <h2 className="mb-0">Mijn privelessen</h2>
        <Link to="/private-lessons/booking" className="btn btn-success">
          Priveles inplannen
        </Link>
      </div>

      <hr className="seperator" />

      <div className="alert alert-light border">
        Klik op "Priveles inplannen" om een gekoppelde docent te kiezen en direct een losse priveles te boeken.<br /> De docent of administratie bevestigt je aanvraag.
      </div>

      {requestedLessons.length > 0 && (
        <>
          <h4 className="mb-3">In afwachting van akkoord</h4>
          {renderLessonsTable(requestedLessons, true)}
        </>
      )}

      <h4 className="mb-3">Komende privelessen</h4>
      {renderLessonsTable(confirmedLessons, true)}

      <h4 className="mb-3">Afgelopen privelessen</h4>
      {renderLessonsTable(pastLessons, false)}
    </div>
  );
}

export default AllMyPrivateLessons;
